/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import {
  Button,
  Checkbox,
  TextField,
  MenuItem,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import CloseIcon from "../../../components/CloseIcon";
import "./AddIndicationPanel.css";
import { useNitroStore } from "../../../store/Store";
import {
  formatShipDate,
  generateMonthOptions,
  logIndication,
  LogIndicationDependencies,
} from "../../../utils/utils";
import IIndicationsRequest from "../../../panels/addFOBIndicationPanel/types/IIndicationsRequest";
import { useIsMobile } from "../../../hooks/useIsMobile";
import cookie from "../../../store/cookie";
import NitroService from "../../../utils/nitro.service";
import { icons } from "../../../svg";
import CopyToClipboardButton from "../../../utils/copyToClipboardButton";

const AddIndicationPanel = () => {
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;
  const {
    product,
    showAddIndicationHistoryPanel,
    setShowAddIndicationHistoryPanel,
    selectedFavoriteOrigin,
    savedNotification,
    failedNotification,
  } = useNitroStore((state) => ({
    product: state.product,
    showAddIndicationHistoryPanel: state.showAddIndicationHistoryPanel,
    setShowAddIndicationHistoryPanel: state.setShowAddIndicationHistoryPanel,
    selectedFavoriteOrigin: state.selectedFavoriteOrigin,
    savedNotification: state.savedNotification,
    failedNotification: state.failedNotification,
  }));

  const [selectedMode, setSelectedMode] = useState<"Truck" | "Rail">("Truck");
  const [truckPrice, setTruckPrice] = useState<string | null>("");
  const [railPrice, setRailPrice] = useState<string | null>("");
  const [contact, setContact] = useState("");
  const [customer, setCustomer] = useState("");
  const [competitor, setCompetitor] = useState("");
  const [comments, setComments] = useState("");

  const disablePostToTeams = useIsMobile(768);
  const [postToTeams, setPostToTeams] = useState(!disablePostToTeams); // false if mobile, true if desktop

  const locationName = _.get(_.first(selectedFavoriteOrigin), "location.name");
  const locationId = _.get(_.first(selectedFavoriteOrigin), "location.id");

  const title = locationName
    ? `Add FOB Indication - ${locationName}`
    : "Add FOB Indication";

  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [competitorOptions, setCompetitorOptions] = useState<any[]>([]);
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [shipWindowStart, setShipWindowStart] = useState(monthOptions[0]);
  const [shipWindowEnd, setShipWindowEnd] = useState(monthOptions[0]);

  const resetForm = useCallback(() => {
    console.log("resetting form");
    setSelectedMode("Truck");
    setContact("");
    setCustomer("");
    setCompetitor("");
    setShipWindowStart(monthOptions[0]);
    setShipWindowEnd(monthOptions[0]);
    setComments("");
    setPostToTeams(!disablePostToTeams); // false if mobile, true if desktop
    setCustomerOptions([]);
  }, [monthOptions, disablePostToTeams]);

  useEffect(() => {
    setTruckPrice(
      String(
        selectedFavoriteOrigin[0]?.months[0].firstPeriod?.truck?.price || "",
      ),
    );
    setRailPrice(
      String(
        selectedFavoriteOrigin[0]?.months[0].firstPeriod?.rail?.price || "",
      ),
    );
  }, [selectedFavoriteOrigin]);
  useEffect(() => {
    if (showAddIndicationHistoryPanel) {
      resetForm();
    }
  }, [resetForm, showAddIndicationHistoryPanel]);

  const debouncedFetchCustomers = useMemo(
    () =>
      _.debounce(async (inputValue: string) => {
        if (inputValue.length < 2) {
          setCustomerOptions([]);
          return;
        }
        setIsCustomerLoading(true);
        try {
          const records = await NitroService.getPartiesLike(inputValue, user);
          setCustomerOptions(records);
        } catch (error) {
          console.error("Failed to fetch customers:", error);
          setCustomerOptions([]);
        } finally {
          setIsCustomerLoading(false);
        }
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedFetchCustomers.cancel();
    };
  }, [debouncedFetchCustomers]);

  useEffect(() => {
    const fetchCompetitors = async () => {
      if (locationId) {
        try {
          const records = await NitroService.getOccupantsByLocationId({
            locationId,
          });
          setCompetitorOptions(records);
        } catch (error) {
          console.error("Failed to fetch competitors:", error);
          setCompetitorOptions([]);
        }
      }
    };

    fetchCompetitors();
  }, [locationId]);
  const refetchIndicationHistory = useNitroStore(
    (state) => state.refetchIndicationHistory,
  );
  const handleLogIndication = async () => {
    const record: IIndicationsRequest = {
      contactName: contact,
      party: customer,
      marketer: user,
      currency: cookie.currency,
      uom: cookie.uom,
      indications: [
        {
          locationId: locationId || "",
          type: "FOB",
          priceType: "single",
          product,
          mode: selectedMode.toUpperCase(),
          modes: [selectedMode.toUpperCase()],
          competitor: customer,
          quantity: 0,
          startDate: formatShipDate(shipWindowStart, "start"),
          endDate: formatShipDate(shipWindowEnd, "end"),
          comments,
          postToTeams,
          prices: [
            {
              origin: locationName || "",
              fobPrice:
                parseFloat(
                  selectedMode === "Truck" ? truckPrice || "" : railPrice || "",
                ) || 0,
            },
          ],
        },
      ],
      isKoch: false,
    };

    const deps: LogIndicationDependencies = {
      disablePostToTeams,
      savedNotification,
      failedNotification,
      reload: () => {
        if (locationId && locationName) {
          refetchIndicationHistory(locationId, locationName);
          setShowAddIndicationHistoryPanel(false);
        }
      },
      close: () => setShowAddIndicationHistoryPanel(false),
    };

    await logIndication(record, deps);
  };

  const handleTruckPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTruckPrice(e.target.value);
    if (e.target.value) {
      setSelectedMode("Truck");
      setRailPrice("");
    }
  };

  const handleRailPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRailPrice(e.target.value);
    if (e.target.value) {
      setSelectedMode("Rail");
      setTruckPrice("");
    }
  };

  if (!showAddIndicationHistoryPanel) {
    return null;
  }

  const hasPrice =
    (truckPrice && truckPrice.trim() !== "") ||
    (railPrice && railPrice.trim() !== "");
  const hasContact = contact && contact.trim() !== "";
  const hasCustomer = customer && customer.trim() !== "";
  const isFormValid = hasPrice && hasContact && hasCustomer;

  return (
    <div id="add-indication-modal-mobile-id" className="add-indication-modal">
      <div className="panel-container">
        <div className="indication-history-header">
          <div className="header-title">{title}</div>
          <div className="close-button-container">
            <CloseIcon
              onClick={() => {
                resetForm();
                setShowAddIndicationHistoryPanel(false);
              }}
            />
          </div>
        </div>

        <div id="fob-body" className="form-body">
          <fieldset className="form-section">
            <legend className="form-label">
              Select Mode & FOB Price
              <span className="required-asterisk">*</span>
            </legend>
            <div className="mode-selection-group">
              <button
                type="button"
                className={`mode-button ${
                  selectedMode === "Truck" ? "selected" : ""
                }`}
                onClick={() => {
                  setSelectedMode("Truck");
                  setRailPrice("");
                }}
              >
                <div className="mode-button-content">
                  <img
                    data-tooltip-id="truck-icon-indication"
                    src={icons.TruckIconFO}
                    alt="TruckIcon"
                  />
                  <span>Truck</span>
                </div>
                <div className="price-wrapper">
                  <TextField
                    className="mode-price-input"
                    variant="standard"
                    value={truckPrice}
                    onChange={handleTruckPriceChange}
                    onFocus={() => {
                      setSelectedMode("Truck");
                      setRailPrice("");
                    }}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      disableUnderline: true,
                    }}
                  />
                </div>
              </button>
              <button
                type="button"
                className={`mode-button ${
                  selectedMode === "Rail" ? "selected" : ""
                }`}
                onClick={() => {
                  setSelectedMode("Rail");
                  setTruckPrice("");
                }}
              >
                <div className="mode-button-content">
                  <img
                    data-tooltip-id="rail-icon-purple-indication"
                    src={icons.RailIconPurple}
                    alt="RailIcon"
                  />
                  <span>Rail</span>
                </div>
                <div className="price-wrapper">
                  <TextField
                    className="mode-price-input"
                    variant="standard"
                    value={railPrice}
                    onChange={handleRailPriceChange}
                    onFocus={() => {
                      setSelectedMode("Rail");
                      setTruckPrice("");
                    }}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      disableUnderline: true,
                    }}
                  />
                </div>
              </button>
            </div>
          </fieldset>

          <div className="form-section">
            <div className="form-row">
              <span id="contact-label" className="form-row-label">
                Contact<span className="required-asterisk">*</span>
              </span>
              <div className="form-input-wrapper">
                <TextField
                  className="custom-textfield-background"
                  id="contact-input"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  InputProps={{ "aria-labelledby": "contact-label" }}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-row">
              <span id="customer-label" className="form-row-label">
                Customer<span className="required-asterisk">*</span>
              </span>
              <div className="form-input-wrapper">
                <Autocomplete
                  id="customer-input"
                  options={customerOptions}
                  getOptionLabel={(option) => option.name || ""}
                  filterOptions={(x) => x}
                  value={
                    customerOptions.find((opt) => opt.name === customer) || null
                  }
                  onChange={(event, newValue) => {
                    setCustomer(newValue ? newValue.name : "");
                    if (newValue) {
                      setCustomerOptions([newValue]);
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      debouncedFetchCustomers(newInputValue);
                    }
                  }}
                  loading={isCustomerLoading}
                  noOptionsText="No customers found"
                  renderInput={(params) => (
                    <TextField
                      className="custom-textfield-background"
                      {...params}
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        "aria-labelledby": "customer-label",
                        endAdornment: (
                          <>
                            {isCustomerLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-row">
              <span id="competitor-label" className="form-row-label">
                Competitor
              </span>
              <div className="form-input-wrapper">
                <Autocomplete
                  id="competitor-input"
                  options={competitorOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    competitorOptions.find((opt) => opt.name === competitor) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setCompetitor(newValue ? newValue.name : "");
                  }}
                  noOptionsText="None"
                  renderInput={(params) => (
                    <TextField
                      className="custom-textfield-background"
                      {...params}
                      size="small"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        "aria-labelledby": "competitor-label",
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-row">
              <span className="form-row-label">
                Ship Window&nbsp;<span className="required-asterisk">*</span>
              </span>
              <div className="form-input-wrapper">
                <div className="ship-window-group">
                  <TextField
                    className="custom-textfield-background"
                    variant="outlined"
                    size="small"
                    fullWidth
                    select
                    value={shipWindowStart}
                    onChange={(e) => setShipWindowStart(e.target.value)}
                  >
                    {monthOptions.map((month) => (
                      <MenuItem key={month} value={month}>
                        {new Date(
                          month.replace("-", " 1, "),
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    className="custom-textfield-background"
                    variant="outlined"
                    size="small"
                    fullWidth
                    select
                    value={shipWindowEnd}
                    onChange={(e) => setShipWindowEnd(e.target.value)}
                  >
                    {monthOptions.map((month) => (
                      <MenuItem key={month} value={month}>
                        {new Date(
                          month.replace("-", " 1, "),
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          year: "numeric",
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-row-comment form-row-vertical">
              <span id="comment-label" className="form-row-label">
                Comments
              </span>
              <div className="form-input-wrapper">
                <TextField
                  className="custom-textfield-background"
                  id="comments-input"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={1}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  InputProps={{ "aria-labelledby": "comment-label" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Copy to Clipboard - Mobile only */}
        {disablePostToTeams && (
          <div className="post-to-teams-container">
            <div style={{ flex: 1 }} />
            <CopyToClipboardButton
              form={
                {
                  getValues: () => ({
                    contactName: contact,
                    party: customer,
                    marketer: user,
                    currency: cookie.currency,
                    uom: cookie.uom,
                    indications: [
                      {
                        locationId: locationId || "",
                        type: "FOB" as const,
                        priceType: "single" as const,
                        product,
                        mode: selectedMode.toUpperCase(),
                        modes: [selectedMode.toUpperCase()],
                        competitor: customer,
                        quantity: 0,
                        startDate: formatShipDate(shipWindowStart, "start"),
                        endDate: formatShipDate(shipWindowEnd, "end"),
                        comments,
                        postToTeams,
                        prices: [
                          {
                            origin: locationName || "",
                            fobPrice:
                              parseFloat(
                                selectedMode === "Truck"
                                  ? truckPrice || ""
                                  : railPrice || "",
                              ) || 0,
                          },
                        ],
                      },
                    ],
                    isKoch: false,
                  }),
                } as any
              }
              index={0}
              variant="contained"
              buttonText="Copy to clipboard"
            />
          </div>
        )}

        {!disablePostToTeams && (
          <div id="post-to-team-mobile" className="post-to-teams-container">
            <img
              data-tooltip-id="MicrosoftTeamsLogo"
              src={icons.MicrosoftTeamsLogo}
              alt="MicrosoftTeamsLogo"
            />
            <span>Post to Teams</span>
            <Checkbox
              checked={postToTeams}
              onChange={(e) => setPostToTeams(e.target.checked)}
              sx={{ padding: "0 8px" }}
            />
          </div>
        )}

        <div id="fob-footer" className="form-footer">
          <div className="footer-buttons">
            <Button
              variant="outlined"
              onClick={() => {
                setShowAddIndicationHistoryPanel(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!isFormValid}
              onClick={() => {
                handleLogIndication();
                resetForm();
                setShowAddIndicationHistoryPanel(false);
              }}
            >
              Log Indication
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddIndicationPanel;
